import React from "react";
import { Briefcase, Users, FileSpreadsheet, Plane, Check, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import CtaSection from "../components/CtaSection";
import { Link } from "react-router-dom";

const SERVICES = [
  {
    icon: Briefcase,
    title: "Wealth Management",
    body:
      "Our wealth management services encompass comprehensive portfolio construction, asset allocation, and ongoing management tailored to your risk profile and financial objectives.",
    bullets: ["Portfolio Construction", "Risk Assessment", "Asset Allocation", "Performance Monitoring"],
    image: "https://images.pexels.com/photos/8442325/pexels-photo-8442325.jpeg",
  },
  {
    icon: Users,
    title: "Family Office Services",
    body:
      "Dedicated family office solutions for multi-generational wealth preservation, succession planning, and comprehensive family governance structures.",
    bullets: ["Succession Planning", "Estate Management", "Family Governance", "Philanthropy Advisory"],
    image: "https://images.pexels.com/photos/8082233/pexels-photo-8082233.jpeg",
  },
  {
    icon: FileSpreadsheet,
    title: "Tax Advisory",
    body:
      "Expert tax planning and optimisation strategies leveraging UK and international tax frameworks to maximise wealth preservation.",
    bullets: ["Tax Optimisation", "International Structuring", "Compliance Management", "Reporting Services"],
    image: "https://images.pexels.com/photos/14523757/pexels-photo-14523757.jpeg",
  },
  {
    icon: Plane,
    title: "Relocation Services",
    body:
      "Comprehensive support for high-net-worth individuals relocating to the UK, including residency permits, property acquisition, and lifestyle management.",
    bullets: ["Residency Permits", "Property Search", "Banking Setup", "Lifestyle Integration"],
    image: "https://images.pexels.com/photos/14936005/pexels-photo-14936005.jpeg",
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="page-fade">
      <PageHero
        eyebrow="Our Expertise"
        title="Practice Areas."
        lede="EMIVIA LIMITED offers a comprehensive suite of financial services designed to protect, grow, and transfer wealth across generations."
        image="https://images.pexels.com/photos/14936005/pexels-photo-14936005.jpeg"
        testId="services-hero"
      />

      <section className="bg-[#070B14]">
        {SERVICES.map((s, i) => {
          const reverse = i % 2 === 1;
          const Icon = s.icon;
          return (
            <div key={s.title} className="border-t border-white/5 first:border-t-0">
              <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 sm:py-28">
                <div className={`grid lg:grid-cols-12 gap-12 lg:gap-20 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}>
                  <Reveal className="lg:col-span-6 [direction:ltr]">
                    <div className="relative aspect-[5/4] overflow-hidden">
                      <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-tr from-[#070B14]/70 via-transparent to-transparent" />
                      <div className="absolute top-6 left-6 text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] bg-[#070B14]/80 px-3 py-1.5 border border-[#D4AF37]/30">
                        0{i + 1} · Service
                      </div>
                    </div>
                  </Reveal>

                  <Reveal delay={120} className="lg:col-span-6 [direction:ltr]">
                    <Icon size={28} strokeWidth={1.25} className="text-[#D4AF37]" />
                    <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
                      {s.title}
                    </h2>
                    <p className="mt-6 text-white/70 text-base sm:text-lg leading-relaxed font-light max-w-xl">
                      {s.body}
                    </p>

                    <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-center gap-3 text-white/80 text-sm">
                          <span className="w-6 h-6 inline-flex items-center justify-center border border-[#D4AF37]/40">
                            <Check size={12} className="text-[#D4AF37]" strokeWidth={2} />
                          </span>
                          {b}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-10 text-[11px] uppercase tracking-[0.32em] text-white/40">
                      Professional services tailored to your needs
                    </div>
                  </Reveal>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      {/* Explore Further */}
      <section className="relative py-24 sm:py-32 border-t border-white/5 bg-[#0B1120]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <Reveal>
            <div className="text-[11px] tracking-[0.32em] uppercase text-[#D4AF37]">Explore Further</div>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="mt-6 font-serif-display text-white text-4xl sm:text-5xl tracking-tight leading-tight">
              Discover Our <span className="italic">Trading</span> Services.
            </h2>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-6 max-w-2xl mx-auto text-white/65 text-base sm:text-lg leading-relaxed font-light">
              From luxury timepieces to gold and crypto assets — explore our strategic investment opportunities.
            </p>
          </Reveal>
          <Reveal delay={360}>
            <Link
              to="/borrowers-investors"
              data-testid="services-explore-cta"
              className="mt-10 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] border border-[#D4AF37] text-[#D4AF37] px-8 py-4 hover:bg-[#D4AF37] hover:text-[#070B14] transition-colors duration-300"
            >
              Borrowers & Investors <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaSection testId="services-cta" />
    </div>
  );
}
